import { get } from 'lodash';
import {
  FEATURES,
  COUNTRIES_FOR_FEATURE,
  USER_DETAIL_FIELDS,
  PROXY_ROLES,
  USER_DOMAINS
} from 'constants/user';
import { countriesAllowDecimalInput } from 'constants/countryCodes';
import { isImpersonating } from 'current/selectors';
import { defaultSupportInfo } from 'constants/support';
import { getCurrency } from './userSelectors';

export const getUserConfigFlags = (state) => get(state, 'user.flags');

export const getRoles = (state) => get(state, 'user.roles');
export const canProxy = (state) => {
  return (
    isImpersonating(state)
    || getRoles(state).some((role) => PROXY_ROLES.includes(role))
  );
};

export const getUnitsOfMeasureTemplate = (state) =>
  get(state, 'user.unitsOfMeasureTemplate');
export const getAllSupportInfo = (state) => get(state, 'user.supportInfo');
export const getAccountCountry = (state) => get(state, 'user.accountCountry'); // Not for units or text, for features only
export const getUserDetails = (state) => {
  const user = {};
  const inState = {
    accountCountry: getAccountCountry(state),
    currency: getCurrency(state),
    unitsOfMeasureTemplate: getUnitsOfMeasureTemplate(state),
    roles: getRoles(state),
    uuid: state.user.uuid,
    email: state.user.email
  };
  // Only include keys that have values to keep isEmpty() useful
  Object.values(USER_DETAIL_FIELDS).forEach((key) => {
    if (inState[key]) {
      user[key] = inState[key];
    }
  });
  return user;
};
export const getProductValidationValues = (state) =>
  get(state, 'user.productValidationValues');

export const getBagSizeForCrop = (state, crop) =>
  get(
    state,
    `user.productValidationValues.PLANTING.["${crop.toUpperCase()}"].BAG_SIZE.quantity`
  );

export const isFeatureAllowed = (state, feature) => {
  return get(COUNTRIES_FOR_FEATURE, feature, []).includes(
    getAccountCountry(state)
  );
};

export const isSsurgoAllowed = (state) => {
  return isFeatureAllowed(state, FEATURES.SSURGO);
};

export const getSupportInfo = (state) => {
  const country = getAccountCountry(state);
  const allSupportInfo = getAllSupportInfo(state);

  /*
    This defaults to the US support info to allow for support info to be shown before the
    userDetails fetch finishes processing. One specific case this handles is if the user
    tries to navigate to a URL after they've changed 'client-id' to something they don't have access to
    In this case, the userDetails fetch will 403 and error and will be provided this default support info.
  */
  return get(allSupportInfo, country, defaultSupportInfo);
};

export const determineProductValidationValueByCropId = (
  productValidationValues,
  cropId
) => {
  return Object.values(productValidationValues.PLANTING).find(
    (val) => Number(val.CROP_ID) === Number(cropId)
  );
};

export const isSupportInfoForAccountCountryAvailable = (state) =>
  Boolean(getSupportInfo(state));

export const isWebPerformancePlatformBetaUser = (state) => {
  return get(state, 'launchDarkly.webPerformancePlatform');
};

export const isManualFlowLowQualityLayersEnabled = (state) =>
  state?.launchDarkly?.scriptCreatorManualFlowLowQualityLayers;

export const isPrescriptionsProgramTagEnabled = (state) => state?.launchDarkly?.scriptCreatorPrescriptionsProgramTag;

export const isOperationalTableViewEnabled = (state) => state?.launchDarkly?.scriptCreatorOperationalTableView;

export const isBulkPrescriptionsEnabled = (state) => state?.launchDarkly?.scriptCreatorBulkPrescriptions;

export const isFieldLevelSeedRecsEnabled = (state) => state?.launchDarkly?.scriptCreatorFieldLevelSeedRecs;

export const isAdvancedScriptingForLatamEnabled = (state) => {
  return get(state, 'user.flags.featureFlagScriptingRxLatam') === 'on';
};

export const isOlEnabled = (state) => {
  return get(state, 'user.flags.featureFlagOlEnabledPhase1') === 'true';
};

export const allowDecimalByCountry = (state) =>
  countriesAllowDecimalInput.includes(getAccountCountry(state));

export const isVolumeTypeSwitchEnabled = (state) =>
  get(
    state,
    'user.flags.cfvwebScriptCreatorSwitchFertilizerApplicationVolumeType'
  ) === 'true';

export const isSingleZoneRecommendationEnabled = (state) =>
  get(state, 'launchDarkly.webScriptCreatorSingleZoneRecommendation') || false;

export const isFieldsFromOlEnabled = (state) => state.launchDarkly?.scriptCreatorFieldsFromOl;

export const areScriptsLoadImprovementsEnabled = (state) => state.launchDarkly?.scriptCreatorScriptsLoadPerformance;

export const getMosaicV3FHIFlag = (state) => state?.launchDarkly?.scriptCreatorMosaicV3Fhi;

export const getMosaicV4SsurgoFlag = (state) => state.launchDarkly?.scriptCreatorMosaicV4Ssurgo;

export const isCropPlanEnabled = (state) => state.launchDarkly?.webCropPlanning;

export const isCropPlanIntegrationEnabled = (state) => state.launchDarkly?.webCropPlanningScripting;
/**
 * Returns the user as it's required by the FieldView Plus Spatial service
 */
export const getUsernameForFVPlusSpatial = (state) => {
  /**
   * Some users have a list of "domains" and "usernames" present within
   * their details received from the User Service. If "precision-planting" is
   * present within a user's "domain" list, a corresponding "username" should
   * also be present and should be used within the FVP endpoints as the user identifier:
   * https://climate.jira.com/browse/PROD-9835?focusedCommentId=480990
   */
  const { domain, email, username } = state.user;

  return domain?.includes(USER_DOMAINS.PRECISION_PLANTING)
    ? username[domain.indexOf(USER_DOMAINS.PRECISION_PLANTING)]
    : email;
};
